import React, { useEffect, useState } from 'react';
import { TitleText, BodyText, BodyTextSmall } from './common';
import { ContinueWithAppleButton } from '../components/ContinueWithAppleButton';
import { ContinueWithEmailButton } from '../components/ContinueWithEmailButton';
import { ContinueWithFacebookButton } from '../components/ContinueWithFacebookButton';
import { ContinueWithGoogleButton } from '../components/ContinueWithGoogleButton';
import { YourPaperCard } from '../components/YourPaperCard';
import { DownloadHeroCard } from '../components/DownloadHeroCard';
import { DownloadPackPreview } from '../components/DownloadPackPreview';
import { Heading } from 'js/design_system/Heading';
import { TextButton } from 'js/design_system/TextButton';
import { BodyLink } from 'js/design_system/link/BodyLink';
import routes from 'js/core_pre_webpack/routes';
import {
  Options as ClickTrackingOptions,
  trackClick,
} from 'js/utils/trackClick';
import styled, { stylesheet } from 'astroturf/react';
import { VerticalSpacer } from '../components/VerticalSpacer';
import { ModalError } from '../components/ModalError';
import {
  ButtonsPageConfigurableText,
  DownloadModalDetail,
  RegistrationParams,
  ModalPage,
  HeaderVariant,
  CtaLayout,
} from '../types';
import { useAutotestHooks } from '../autotest/hooks';

type Props = {
  onAttempt?: () => void;
  onAuthorized?: () => void;
  onSuccess?: (redirect?: string) => void;
  onError: (error?: { errorMessage?: string; errorCodes?: string[] }) => void;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  errorMessage?: string;
  onEmailButtonClick: () => void;
  show: boolean;
  clickTrackingContext?: Partial<ClickTrackingOptions>;
  text: ButtonsPageConfigurableText;
  registrationParams: RegistrationParams;
  setPage: React.Dispatch<React.SetStateAction<ModalPage>>;
  // Structural sockets (default to today's behavior).
  headerVariant?: HeaderVariant;
  ctaLayout?: CtaLayout;
  // Work context, read only by headerVariant: 'yourPaperCard'.
  workTitle?: string;
  authorName?: string;
  workThumbnailUrl?: string;
  workYear?: number;
  workViewCount?: number;
  downloadModal?: DownloadModalDetail;
};

const ButtonStyleContext = styled.div<{ show: boolean }>`
  display: none;
  flex-direction: column;

  &.show {
    display: flex;
  }
`;

const SharedButtonStyles = stylesheet`
  .margins {
    margin-top: 8px;
    margin-bottom: 8px;
    position: relative; // so the icons can be absolutely positioned
  }

  .margins img {
    position: absolute;
    left: 24px;
    bottom: 20px;
    height: 24px;
  }
`;

const ExpanderButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  margin: 8px auto;
  cursor: pointer;
  color: var(--text-primary, #141413);
  font-size: 14px;
  text-decoration: underline;
  font-family: inherit;

  &:hover {
    opacity: 0.8;
  }
`;

const DownloadTitleRow = styled.div<{ spacing?: 'pack' }>`
  padding-bottom: var(--spacing-md-40, 40px);

  &.spacing-pack {
    padding-bottom: var(--spacing-sm-24, 24px);
  }
`;

const DownloadTitle = styled(Heading).attrs({
  size: 'md' as const,
  fontFamily: 'serif' as const,
  as: 'h2' as const,
})`
  @import 'stylesheets/design_system/breakpoint';

  text-align: center;
  letter-spacing: var(--type-serif-md-letter-spacing, -1px);
  color: var(--text-primary, #141413);

  @include breakpoint(small) {
    font-size: 40px;
    line-height: 40px;
  }
`;

const DownloadCardWrap = styled.div`
  width: 100%;
  max-width: 560px;
  align-self: center;
`;

const DownloadButtonsColumn = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: center;
  width: 460px;
  max-width: 100%;

  @include breakpoint(small, medium) {
    width: 100%;
  }
`;

const DownloadExpanderWrap = styled.div`
  padding-top: var(--spacing-sm-24, 24px);
`;

const DownloadHeroStyles = stylesheet`
  .googlehero {
    position: relative;
    width: 100%;

    &:global(.ds2-5-button) {
      justify-content: center;
      width: 100%;
      height: 64px;
      padding: var(--spacing-xs-12, 12px) var(--spacing-sm-24, 24px);
      border-radius: 16px;
      font-family: var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
      font-size: var(--type-body-md-bold-size, 16px);
      line-height: var(--type-body-md-bold-line-height, 20px);
      font-weight: var(--type-body-md-bold-font-weight, 600);
    }

    &:global(.ds2-5-button.ds2-5-button--secondary),
    &:global(.ds2-5-button.ds2-5-button--secondary):not(:disabled):hover,
    &:global(.ds2-5-button.ds2-5-button--secondary):not(:disabled):active {
      background: var(--button-primary-fill, #0645b1);
      border-color: var(--button-primary-fill, #0645b1);
      color: var(--text-white, #ffffff);
    }

    img {
      position: absolute;
      top: 50%;
      left: var(--spacing-sm-24, 24px);
      box-sizing: content-box;
      width: 20px;
      height: 20px;
      padding: var(--spacing-xs-8, 8px);
      background: var(--background-white, #ffffff);
      border-radius: var(--border-radius-radius-round, 104px);
      transform: translateY(-50%);
    }
  }
`;

export const ButtonsPage: React.FC<Props> = (props) => {
  const {
    onAttempt,
    onSuccess,
    onError,
    loading,
    setLoading,
    errorMessage,
    onEmailButtonClick,
    show,
    clickTrackingContext,
    text,
    registrationParams,
    setPage,
    headerVariant = 'default',
    ctaLayout = 'all',
    workTitle,
    authorName,
    workThumbnailUrl,
    workYear,
    workViewCount,
    downloadModal,
  } = props;

  const downloadHero =
    headerVariant === 'downloadHero' && downloadModal?.title
      ? downloadModal
      : undefined;
  const resolvedHeaderVariant: HeaderVariant =
    headerVariant === 'downloadHero' && !downloadHero
      ? 'yourPaperCard'
      : headerVariant;
  const ButtonsColumn = downloadHero ? DownloadButtonsColumn : React.Fragment;

  const autotestHooks = useAutotestHooks();
  const autotestSlots = autotestHooks?.slots;
  const ctaDefaultExpanded = autotestHooks?.ctaDefaultExpanded;

  const [expanded, setExpanded] = useState(ctaDefaultExpanded ?? false);

  // The page stays mounted across modal opens, so the useState initializer
  // alone can't apply a pre-open arriving with a later open event.
  useEffect(() => {
    if (ctaDefaultExpanded) {
      setExpanded(true);
    }
  }, [ctaDefaultExpanded]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userInChina = (window as any).userInChina === 'true';

  // Feature flag for disabling Facebook login - hardcoded to true for testing
  const facebookLoginDisabled =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).facebookLoginDisabled === 'true';

  const handleFacebookButtonClick = () => {
    if (facebookLoginDisabled) {
      // Navigate to the facebook-disabled page
      setPage('facebook-disabled');
    }
    // If not disabled, the ContinueWithFacebookButton handles its own click logic
  };

  const handleExpanderClick = () => {
    setExpanded(true);
    // Spread context first so `source` is not overridden by an opener source.
    trackClick({
      ...clickTrackingContext,
      source: 'login_modal_expander_open',
    });
  };

  const googleButton = !userInChina && (
    <ContinueWithGoogleButton
      onAttempt={onAttempt}
      onError={onError}
      onSuccess={onSuccess}
      disabled={loading}
      className={
        downloadHero
          ? DownloadHeroStyles.googlehero
          : SharedButtonStyles.margins
      }
      setLoading={setLoading}
      clickTrackingContext={clickTrackingContext}
      buttonText={text.googleButton}
      registrationParams={registrationParams}
    />
  );

  const appleButton = (
    <ContinueWithAppleButton
      onAttempt={onAttempt}
      onError={onError}
      onSuccess={onSuccess}
      disabled={loading}
      className={SharedButtonStyles.margins}
      setLoading={setLoading}
      clickTrackingContext={clickTrackingContext}
      buttonText={text.appleButton}
      registrationParams={registrationParams}
    />
  );

  const facebookButton =
    !userInChina &&
    (facebookLoginDisabled ? (
      // When Facebook login is disabled, render a button that looks the same but navigates to the disabled page
      <ContinueWithFacebookButton
        onClick={handleFacebookButtonClick}
        onError={onError}
        onSuccess={onSuccess}
        disabled={loading}
        className={SharedButtonStyles.margins}
        setLoading={setLoading}
        clickTrackingContext={clickTrackingContext}
        buttonText={text.facebookButton}
        registrationParams={registrationParams}
      />
    ) : (
      // Normal Facebook OAuth flow
      <ContinueWithFacebookButton
        onAttempt={onAttempt}
        onError={onError}
        onSuccess={onSuccess}
        disabled={loading}
        className={SharedButtonStyles.margins}
        setLoading={setLoading}
        clickTrackingContext={clickTrackingContext}
        buttonText={text.facebookButton}
        registrationParams={registrationParams}
      />
    ));

  const emailButton = (
    <ContinueWithEmailButton
      onClick={onEmailButtonClick}
      disabled={loading}
      className={SharedButtonStyles.margins}
      buttonText={text.emailButton}
      userInChina={userInChina}
    />
  );

  return (
    <ButtonStyleContext show={show}>
      {autotestSlots?.aboveHeader}
      {downloadHero ? (
        <>
          <DownloadTitleRow
            spacing={downloadHero.mode === 'pdf_pack' ? 'pack' : undefined}
          >
            <DownloadTitle>{downloadHero.title}</DownloadTitle>
          </DownloadTitleRow>
          {downloadHero.mode === 'pdf_pack' && downloadHero.packPreview ? (
            <DownloadPackPreview
              packPreview={downloadHero.packPreview}
              card={{
                workTitle: downloadHero.workTitle,
                authorLine: downloadHero.authorLine,
                publicationLine: downloadHero.publicationLine,
                thumbnailUrl: downloadHero.workThumbnailUrl,
              }}
            />
          ) : (
            <DownloadCardWrap>
              <DownloadHeroCard
                mode="pdf_only"
                workTitle={downloadHero.workTitle}
                authorLine={downloadHero.authorLine}
                publicationLine={downloadHero.publicationLine}
                thumbnailUrl={downloadHero.workThumbnailUrl}
              />
            </DownloadCardWrap>
          )}
        </>
      ) : resolvedHeaderVariant === 'yourPaperCard' ? (
        <>
          <YourPaperCard
            paperTitle={workTitle?.trim() || 'this paper'}
            authorName={authorName}
            thumbnailUrl={workThumbnailUrl}
            year={workYear}
            viewCount={workViewCount}
          />
          {/* Opt-in subheader (e.g. speed lever), below the card. */}
          {text.subheader && (
            <>
              <VerticalSpacer height={12} />
              <BodyText>{text.subheader}</BodyText>
            </>
          )}
        </>
      ) : (
        // Truthiness guards so blanked slots collapse cleanly (quiet mode).
        <>
          {text.header && <TitleText>{text.header}</TitleText>}
          {text.subheader && <BodyText>{text.subheader}</BodyText>}
        </>
      )}
      {autotestSlots?.underHeader}
      <ModalError errorMessage={errorMessage} show={Boolean(errorMessage)} />
      <ButtonsColumn>
        <VerticalSpacer
          height={resolvedHeaderVariant === 'default' ? 32 : 24}
        />
        {downloadHero ? (
          <BodyTextSmall>
            By clicking {text.disclaimerVerb}, you agree to our{' '}
            <BodyLink size="sm" underline href={routes.terms_path()}>
              Terms of Use
            </BodyLink>{' '}
            and{' '}
            <BodyLink size="sm" underline href={routes.privacy_path()}>
              Privacy Policy
            </BodyLink>
          </BodyTextSmall>
        ) : (
          <BodyTextSmall>
            By clicking {text.disclaimerVerb}, you agree to our{' '}
            <a href={routes.terms_path()}>Terms of Use</a> and{' '}
            <a href={routes.privacy_path()}>Privacy Policy</a>
          </BodyTextSmall>
        )}
        <VerticalSpacer height={downloadHero ? 24 : 16} />
        {autotestSlots?.preCta}
        {ctaLayout === 'googleOnlyExpander' ? (
          <>
            {googleButton}
            {!expanded &&
              (downloadHero ? (
                <DownloadExpanderWrap>
                  <TextButton size="lg" onClick={handleExpanderClick}>
                    Other ways to download
                  </TextButton>
                </DownloadExpanderWrap>
              ) : (
                <>
                  <VerticalSpacer height={8} />
                  <ExpanderButton type="button" onClick={handleExpanderClick}>
                    Other ways to download
                  </ExpanderButton>
                </>
              ))}
            {expanded && (
              <>
                {appleButton}
                {facebookButton}
                {emailButton}
              </>
            )}
          </>
        ) : (
          <>
            {googleButton}
            {appleButton}
            {facebookButton}
            {emailButton}
          </>
        )}
      </ButtonsColumn>
      {autotestSlots?.underCta}
    </ButtonStyleContext>
  );
};
